import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { DataTable } from "../components/DataTable";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency, formatMonthLabel, formatPct } from "../lib/dates";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#10B981", "#F59E0B", "#EF4444", "#3B82F6", "#94A3B8", "#8B5CF6"];

export default function Accounting() {
  const { data, loading, error } = useDashboard("accounting");

  return (
    <>
      <Topbar title="Contabilidad" subtitle="Ingresos, gastos y cartera" />
      <div className="p-6 space-y-6" data-testid="accounting-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KpiCard testId="kpi-revenue-acc" label="Ingresos" value={formatCurrency(data.kpis.revenue)} />
              <KpiCard testId="kpi-expense-acc" label="Gastos" value={formatCurrency(data.kpis.expense)} />
              <KpiCard testId="kpi-margin-acc" label="Margen" value={formatCurrency(data.kpis.margin)} accent={data.kpis.margin >= 0 ? "success" : "danger"} />
              <KpiCard testId="kpi-ar" label="Cobros pendientes" value={formatCurrency(data.kpis.open_ar)} accent="warning" />
              <KpiCard testId="kpi-ap" label="Pagos pendientes" value={formatCurrency(data.kpis.open_ap)} accent="warning" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel title="Evolución P&L" subtitle="Ingresos, gastos y margen" className="lg:col-span-2" testId="chart-pnl">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonthLabel} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="#002FA7" fill="#002FA7" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="gastos" name="Gastos" stroke="#EF4444" fill="#EF4444" fillOpacity={0.12} />
                      <Area type="monotone" dataKey="margen" name="Margen" stroke="#10B981" fill="#10B981" fillOpacity={0.08} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Estado de pago" subtitle="Importes por estado" testId="chart-payment-status">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.payment_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                        {data.payment_status.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <Panel title="Facturas recientes" subtitle="Ventas y compras" testId="table-invoices">
              <DataTable
                testId="invoices-table"
                filename="facturas.csv"
                rows={data.recent_invoices}
                columns={[
                  { key: "name", label: "Número", accessor: "name" },
                  { key: "type", label: "Tipo", accessor: "type" },
                  { key: "partner", label: "Tercero", accessor: "partner" },
                  { key: "date", label: "Fecha", accessor: "date" },
                  {
                    key: "amount", label: "Importe",
                    accessor: (r) => r.amount,
                    render: (r) => <span className="tabular-nums font-medium">{formatCurrency(r.amount)}</span>,
                  },
                  { key: "payment_state", label: "Pago", accessor: "payment_state" },
                ]}
              />
            </Panel>
          </>
        )}
      </div>
    </>
  );
}
